/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: CreateEmployeeDialog.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-EMPLOYEES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * CreateEmployeeDialog - Диалог создания сотрудника с превью карточки команды.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - Вертикальный скролл длинной формы через formDialogContentSx.
 * LAST_CHANGE: 2026-07-14 - UX: шапка команды, live-превью, секции формы, убрано дублирующее поле проекта.
 * LAST_CHANGE: 2026-07-13 - Общие хуки useDialogFormReady и useBlurValidatedField.
 * END_CHANGE_SUMMARY
 */

import { useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import { alpha } from '@mui/material/styles';
import FormDialogHeader, {
  formDialogActionsSx,
  formDialogContentSx,
  formDialogFormSx,
  formDialogPaperSlotProps,
} from '../../../components/FormDialogHeader';
import FormSection from '../../../components/FormSection';
import ProjectAvatar from '../../../components/ProjectAvatar';
import RoleWorkloadBar from '../../../components/RoleWorkloadBar';
import { getColorByName } from '../../../utils/projectColors';
import { useDialogFormReady } from '../../../hooks/useDialogFormReady';
import { useBlurValidatedField } from '../../../hooks/useBlurValidatedField';
import { isNonEmptyTrimmed } from '../../../utils/formValidators';
import ProjectRoleSelect from './ProjectRoleSelect';
import EmployeeAbsenceFields, {
  areAllAbsencesValid,
  buildAbsencesPayload,
} from './EmployeeAbsenceFields';

/* START_METHOD_CreateEmployeeDialog */
/* START_CONTRACT: CreateEmployeeDialog
 * PURPOSE: Диалог создания сотрудника (имя, роль, отсутствия) с валидацией и обратной связью по отправке.
 * INPUTS: { open: boolean, onClose: ()=>void, onSubmit: (payload)=>Promise, project: Project, availableRoles: ProjectRole[] }.
 * OUTPUTS: { JSX.Element - диалог }.
 * SIDE_EFFECTS: вызов onSubmit (HTTP наверх); локальный state.
 * LINKS: M-FE-EMPLOYEES
 * END_CONTRACT: CreateEmployeeDialog
 */
export default function CreateEmployeeDialog({
  open,
  onClose,
  onSubmit,
  project,
  availableRoles,
}) {
  const firstFieldRef = useRef(null);
  const { formReady, resetFormReady, dialogTransitionSlotProps } = useDialogFormReady({ firstFieldRef });
  const nameField = useBlurValidatedField('', isNonEmptyTrimmed, formReady);
  const [selectedRole, setSelectedRole] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const trimmedName = nameField.value.trim();
  const previewName = trimmedName || 'Имя сотрудника';
  const roleColor = selectedRole ? getColorByName(selectedRole.name) : null;

  const canSave = nameField.isValid
    && selectedRole != null
    && areAllAbsencesValid(absences);

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (!canSave || loading) {
      nameField.touch();
      return;
    }
    setLoading(true);
    setSubmitError('');
    try {
      await onSubmit({
        name: trimmedName,
        projectRoleId: selectedRole.id,
        absences: buildAbsencesPayload(absences),
      });
      handleClose();
    } catch (err) {
      console.warn('[CreateEmployeeDialog][handleSubmit][SUBMIT_FAIL] create employee failed', {
        status: err.response?.status,
      });
      setSubmitError(
        err.response?.data?.message || 'Не удалось создать сотрудника. Попробуйте ещё раз.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    nameField.reset('');
    resetFormReady();
    setSelectedRole(null);
    setAbsences([]);
    setSubmitError('');
    onClose();
  };

  const nameHelperText = nameField.showError
    ? 'Введите имя сотрудника'
    : 'Как человек появится в составе команды';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="create-employee-title"
      slotProps={formDialogPaperSlotProps(dialogTransitionSlotProps)}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={formDialogFormSx}
      >
        <FormDialogHeader
          overline="Команда"
          title="Новый сотрудник"
          titleId="create-employee-title"
          subtitle={
            project?.name
              ? `Добавление в проект «${project.name}». Каждая роль может быть назначена одному человеку.`
              : 'Назначьте роль и при необходимости укажите периоды отсутствия.'
          }
        />

        <DialogContent sx={formDialogContentSx}>
          <Collapse in={Boolean(submitError)} unmountOnExit>
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
              {submitError}
            </Alert>
          </Collapse>

          <Paper
            variant="outlined"
            aria-hidden
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              mb: 2.5,
              borderColor: trimmedName && roleColor ? alpha(roleColor, 0.25) : 'divider',
              bgcolor: roleColor ? alpha(roleColor, 0.04) : 'background.default',
            }}
          >
            <ProjectAvatar name={trimmedName} size={36} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                noWrap
                color={trimmedName ? 'text.primary' : 'text.disabled'}
              >
                {previewName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {selectedRole ? selectedRole.name : 'Выберите проектную роль'}
              </Typography>
              {selectedRole && (
                <Box sx={{ mt: 0.75 }}>
                  <RoleWorkloadBar
                    value={selectedRole.workloadPercent}
                    accentColor={roleColor}
                    size="lg"
                  />
                </Box>
              )}
              {!selectedRole && (
                <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25, display: 'block' }}>
                  Превью загрузки появится после выбора роли
                </Typography>
              )}
            </Box>
            {selectedRole && (
              <Chip
                label={selectedRole.shortName}
                size="small"
                sx={{
                  height: 22,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  bgcolor: roleColor,
                  color: '#fff',
                  flexShrink: 0,
                }}
              />
            )}
          </Paper>

          <FormSection label="О сотруднике" first>
            <TextField
              inputRef={firstFieldRef}
              required
              fullWidth
              label="Имя"
              placeholder="Например, Анна Иванова"
              value={nameField.value}
              onChange={nameField.onChange}
              onBlur={nameField.onBlur}
              size="small"
              disabled={loading}
              error={nameField.showError}
              helperText={nameHelperText}
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
            <ProjectRoleSelect
              label="Проектная роль"
              roles={availableRoles}
              value={selectedRole}
              onChange={setSelectedRole}
              disabled={loading}
            />
          </FormSection>

          <FormSection label="Отсутствия">
            <EmployeeAbsenceFields
              absences={absences}
              onChange={setAbsences}
              disabled={loading}
            />
          </FormSection>
        </DialogContent>

        <DialogActions sx={formDialogActionsSx}>
          <Button size="small" onClick={handleClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="small"
            loading={loading}
            disabled={!canSave}
          >
            Добавить в команду
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
/* END_METHOD_CreateEmployeeDialog */
